import { useMutation, useQueryClient } from '@tanstack/react-query';
import { rsvpToEvent, type RsvpStatus } from '../../services/events';

interface RsvpButtonsProps {
  eventId: string;
  currentStatus?: RsvpStatus | null;
}

export default function RsvpButtons({ eventId, currentStatus }: RsvpButtonsProps) {
  const queryClient = useQueryClient();

  const rsvpMutation = useMutation({
    mutationFn: (status: RsvpStatus) => rsvpToEvent(eventId, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['event', eventId] });
      queryClient.invalidateQueries({ queryKey: ['events'] });
      queryClient.invalidateQueries({ queryKey: ['user-rsvp', eventId] });
      queryClient.invalidateQueries({ queryKey: ['pending-rsvp-events'] });
    },
  });

  const handleRsvp = (status: RsvpStatus) => {
    rsvpMutation.mutate(status);
  };

  const buttons: Array<{ status: RsvpStatus; label: string; color: string }> = [
    { status: 'attending', label: 'Jeg deltager', color: 'bg-primary-600 text-white' },
    { status: 'maybe', label: 'Måske', color: 'bg-yellow-500 text-white' },
    { status: 'not_attending', label: 'Jeg melder afbud', color: 'bg-gray-400 text-white' },
  ];

  return (
    <div>
      <p className="text-sm font-semibold text-gray-700 mb-2">Deltager du?</p>
      <div className="flex gap-1.5 flex-wrap">
      {buttons.map(({ status, label, color }) => {
        const isSelected = currentStatus === status;
        const isLoading = rsvpMutation.isPending;

        return (
          <button
            key={status}
            onClick={() => handleRsvp(status)}
            disabled={isLoading}
            className={`px-3 py-1.5 text-sm rounded-lg font-medium transition-all ${
              isSelected
                ? color
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            } ${
              isLoading ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            {isLoading && rsvpMutation.variables === status ? (
              <span className="flex items-center gap-1">
                <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
              </span>
            ) : (
              label
            )}
          </button>
        );
      })}
      </div>
    </div>
  );
}
